class HalloweenCollarProduct {
  constructor(product) {
    this.product = product;
    this.mainImage = product.querySelector('[data-collar-main-image]');
    this.swatches = Array.from(product.querySelectorAll('[data-collar-swatch]'));
    this.optionButtons = Array.from(product.querySelectorAll('[data-collar-option]'));
    this.planPanel = product.querySelector('[data-collar-plans]');
    this.planCards = Array.from(product.querySelectorAll('[data-collar-plan]'));
    this.planCloseButton = product.querySelector('[data-collar-plans-close]');
    this.cartButton = product.querySelector('[data-collar-cart]');
    this.colorNameElements = Array.from(product.querySelectorAll('[data-collar-color-name]'));
    this.price = product.querySelector('[data-collar-price]');
    this.comparePrice = product.querySelector('[data-collar-compare-price]');
    this.variants = this.readVariants();
    this.currencyCode = product.closest('[data-halloween-collar-feature]')?.dataset.currency || window.Shopify?.currency?.active || 'USD';
    this.selectedOptions = {};
    this.activePlanCard = null;
    this.isCollarGo = product.classList.contains('halloween-collar-product--go');
    if (!this.cartButton || this.variants.length === 0) return;

    this.handleClick = this.handleClick.bind(this);
    this.product.addEventListener('click', this.handleClick);
    this.selectDefaults();
  }

  readVariants() {
    const dataElement = this.product.querySelector('[data-collar-variants]');
    if (!dataElement) return [];
    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      return [];
    }
  }

  // 为每个商品实例选择首个可用规格，保证同页两个商品互不干扰。
  selectDefaults() {
    const standardOptions = [...this.optionButtons, ...this.swatches];
    const positions = new Set(standardOptions.map((button) => button.dataset.optionPosition));
    positions.forEach((position) => {
      const firstButton = standardOptions.find((button) => button.dataset.optionPosition === position);
      if (firstButton) this.selectOption(firstButton, false);
    });
    this.updatePlanCards();
    this.selectPlan(this.planCards.find((card) => !card.disabled));
    this.updateProductPrice();
  }

  handleClick(event) {
    const swatch = event.target.closest('[data-collar-swatch]');
    if (swatch && this.product.contains(swatch)) {
      this.selectOption(swatch, true);
      if (!this.isCollarGo) this.showPlans();
      return;
    }

    const optionButton = event.target.closest('[data-collar-option]');
    if (optionButton && this.product.contains(optionButton)) {
      this.selectOption(optionButton, false);
      if (this.isCollarGo) this.showPlans();
      return;
    }

    const planCloseButton = event.target.closest('[data-collar-plans-close]');
    if (planCloseButton && this.product.contains(planCloseButton)) return this.hidePlans();

    const quantityButton = event.target.closest('[data-quantity-action]');
    if (quantityButton && this.product.contains(quantityButton)) {
      const planCard = quantityButton.closest('[data-collar-plan]');
      if (planCard && this.product.contains(planCard)) return this.changeQuantity(planCard, quantityButton.dataset.quantityAction);
    }

    const planCard = event.target.closest('[data-collar-plan]');
    if (planCard && this.product.contains(planCard)) {
      this.selectPlan(planCard);
      return;
    }

    if (event.target.closest('[data-collar-cart]')) {
      if (!this.isCollarGo && this.planPanel?.hidden) {
        this.showPlans();
      }
      this.handleCartClick();
    }
  }

  selectOption(button, updateImage) {
    const position = button.dataset.optionPosition;
    const value = button.dataset.optionValue || button.dataset.color || '';
    if (!position || !value) return;

    this.selectedOptions[position] = value;
    this.product.querySelectorAll(`[data-option-position="${position}"]`).forEach((item) => {
      if (item.matches('[data-collar-plan]')) return;
      const isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', String(isActive));
    });

    if (button.matches('[data-collar-swatch]')) this.updateColorName(button);

    this.updatePlanCards();
    const selectedPlan = this.planCards.find((card) => card.classList.contains('is-active') && !card.disabled);
    this.selectPlan(selectedPlan || this.planCards.find((card) => !card.disabled));
    if (updateImage) this.updateMainImage(button);
  }

  updateMainImage(button) {
    const nextImage = button.dataset.image;
    if (!nextImage || !this.mainImage || this.mainImage.currentSrc === nextImage || this.mainImage.src === nextImage) return;
    const imageLoader = new Image();
    imageLoader.onload = () => requestAnimationFrame(() => {
      this.mainImage.src = nextImage;
      this.mainImage.alt = button.dataset.alt || this.product.dataset.productTitle || 'SATELLAI product';
    });
    imageLoader.src = nextImage;
  }

  updateColorName(button) {
    const colorName = button.dataset.color || button.dataset.optionValue || '';
    if (!colorName) return;
    this.colorNameElements.forEach((element) => { element.textContent = colorName; });
  }

  showPlans() {
    if (!this.planPanel || !this.planPanel.hidden) return;
    this.planPanel.hidden = false;
    requestAnimationFrame(() => this.planPanel.classList.add('is-visible'));
  }

  hidePlans() {
    if (!this.planPanel || this.planPanel.hidden) return;
    this.planPanel.classList.remove('is-visible');
    window.setTimeout(() => { this.planPanel.hidden = true; }, 220);
    this.cartButton?.focus();
  }

  selectPlan(planCard) {
    if (!planCard) return;
    this.activePlanCard = planCard;
    this.selectedOptions[planCard.dataset.optionPosition] = planCard.dataset.plan || '';
    this.planCards.forEach((card) => {
      const isActive = card === planCard;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-pressed', String(isActive));
    });
    this.cartButton.classList.add('is-ready');
    this.cartButton.setAttribute('aria-label', `Add selected ${this.product.dataset.productTitle || 'SATELLAI product'} to cart`);
    this.updateProductPrice();
  }

  updatePlanCards() {
    this.planCards.forEach((card) => {
      const variant = this.findVariant({ [card.dataset.optionPosition]: card.dataset.plan });
      card.dataset.variantId = variant?.id || '';
      card.toggleAttribute('disabled', !variant?.available);
      const price = card.querySelector('[data-plan-price]');
      const comparePrice = card.querySelector('[data-plan-compare-price]');
      const saving = card.querySelector('[data-plan-saving]');
      if (price) price.textContent = variant ? this.formatMoney(variant.price) : '';
      if (comparePrice) {
        comparePrice.textContent = variant?.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
        comparePrice.hidden = !variant?.compare_at_price || variant.compare_at_price <= variant.price;
      }
      if (saving) {
        const savingAmount = (variant?.compare_at_price || 0) - (variant?.price || 0);
        saving.textContent = savingAmount > 0 ? `Save ${this.formatMoney(savingAmount)}` : '';
        saving.hidden = savingAmount <= 0;
      }
    });
  }

  // Keep the headline price aligned with the exact variant that will be added to cart.
  updateProductPrice() {
    const variant = this.findVariant();
    if (!variant || !this.price) return;

    this.price.textContent = this.formatMoney(variant.price);
    if (!this.comparePrice) return;

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    this.comparePrice.textContent = hasDiscount ? this.formatMoney(variant.compare_at_price) : '';
    this.comparePrice.hidden = !hasDiscount;
  }

  findVariant(extraOptions = {}) {
    const selectedOptions = { ...this.selectedOptions, ...extraOptions };
    return this.variants.find((variant) => variant.options.every((value, index) => selectedOptions[String(index + 1)] === value));
  }

  changeQuantity(planCard, action) {
    const currentQuantity = Number.parseInt(planCard.dataset.quantity || '1', 10) || 1;
    const nextQuantity = action === 'increase' ? currentQuantity + 1 : Math.max(1, currentQuantity - 1);
    planCard.dataset.quantity = String(nextQuantity);
    const quantityDisplay = planCard.querySelector('[data-collar-quantity]');
    if (quantityDisplay) quantityDisplay.textContent = nextQuantity;
  }

  async handleCartClick() {
    const variant = this.findVariant();
    if (!variant || !variant.available || this.cartButton.disabled) return;
    this.cartButton.disabled = true;
    this.cartButton.classList.add('is-loading');

    try {
      const cartHelper = window.Alpine?.store('xCartHelper');
      window.Alpine?.store('xModal')?.setActiveElement(this.cartButton.id);
      const sectionsToRender = cartHelper?.getSectionsToRender?.() || [];
      const selectedQuantity = Number.parseInt(this.activePlanCard?.dataset.quantity || '1', 10) || 1;
      const requestBody = { items: [{ id: variant.id, quantity: selectedQuantity }] };
      if (sectionsToRender.length > 0) {
        requestBody.sections = sectionsToRender.map((section) => section.id);
        requestBody.sections_url = window.location.pathname;
      }
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) throw new Error('Unable to add this item to the cart.');
      const addedItem = await response.json();

      // 使用主题购物车组件返回的 sections 更新抽屉内容。
      if (addedItem.sections && sectionsToRender.length > 0) {
        sectionsToRender.forEach((section) => {
          const sectionElement = document.querySelector(section.selector);
          const sectionHtml = addedItem.sections[section.id];
          if (sectionElement && sectionHtml) sectionElement.innerHTML = this.getSectionInnerHtml(sectionHtml, section.selector);
        });
      }
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      document.dispatchEvent(new CustomEvent('gtm:product-added', { detail: { response: addedItem, button: this.cartButton } }));
      const cartCount = Number.parseInt(document.querySelector('#cart-icon-bubble span')?.textContent || '', 10);
      if (cartHelper && Number.isFinite(cartCount)) cartHelper.currentItemCount = cartCount;
      window.setTimeout(() => window.Alpine?.store('xMiniCart')?.openCart?.(), 100);
      this.cartButton.classList.add('is-added');
      window.setTimeout(() => this.cartButton.classList.remove('is-added'), 1600);
    } catch (error) {
      this.cartButton.classList.add('has-error');
      window.setTimeout(() => this.cartButton.classList.remove('has-error'), 2000);
    } finally {
      this.cartButton.disabled = false;
      this.cartButton.classList.remove('is-loading');
    }
  }

  formatMoney(cents) {
    const amount = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.currencyCode, minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  getSectionInnerHtml(html, selector) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    return parsedHtml.querySelector(selector)?.innerHTML || html;
  }
}

const initializeHalloweenCollarProducts = (container = document) => {
  container.querySelectorAll('[data-collar-product]').forEach((product) => {
    if (product.dataset.initialized === 'true') return;
    product.dataset.initialized = 'true';
    new HalloweenCollarProduct(product);
  });
};

// Initialize predictably; requestIdleCallback can be postponed indefinitely on this media-heavy page.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHalloweenCollarProducts(), { once: true });
} else {
  initializeHalloweenCollarProducts();
}

document.addEventListener('shopify:section:load', (event) => initializeHalloweenCollarProducts(event.target));
